// client/src/analytics.js
// Analytics tracker with immediate flush for navigation events

import flex from './api/FlexFetch.js'
import { getApiBaseUrl } from './api/FlexFetch.js'

const eventQueue = []
const FLUSH_INTERVAL_MS = 5000 // Reduced to 5 seconds
const navigationTags = ['navigation.', 'match.', 'admin.', 'user.', 'league.'] // Tags that might trigger navigation

function flushEvents(forceSync = false) {
  if (eventQueue.length === 0) return

  const eventsToSend = eventQueue.splice(0, eventQueue.length)
  console.log(
    'Analytics: flushing',
    eventsToSend.length,
    'events:',
    eventsToSend,
    forceSync ? '(SYNC)' : '(ASYNC)',
  )

  if (eventsToSend.length > 0) {
    console.log(
      'Analytics: first event details:',
      JSON.stringify(eventsToSend[0], null, 2),
    )
  }

  const payload = JSON.stringify(eventsToSend)

  // Use sendBeacon for synchronous sending during page unload
  if (forceSync && navigator.sendBeacon) {
    console.log('Analytics: using sendBeacon for sync flush')
    const blob = new Blob([payload], { type: 'application/json' })

    // Use getApiBaseUrl() for consistency with all other API calls
    const baseUrl = getApiBaseUrl()
    const url = `${baseUrl}/api/1.0/analytics/post`
    navigator.sendBeacon(url, blob)
    return
  }

  // Use FlexFetch for regular async requests
  flex
    .flexFetch('/api/1.0/analytics/post', {
      method: 'POST',
      body: payload,
    })
    .then((response) => {
      console.log('Analytics: response status:', response.status)
      if (!response.ok) {
        return response.text().then((text) => {
          console.error('Analytics: error response:', text)
          throw new Error(`HTTP ${response.status}: ${text}`)
        })
      }
      return response.json()
    })
    .then((data) => {
      console.log('Analytics: success response:', data)
      // TODO: simplify to `data.data?.user_id` once all environments have deployed
      // the strict-contract response envelope (api-contract-analytics migration).
      const userId = data.data?.user_id ?? data.user_id
      if (userId) {
        console.log('Analytics: events saved with user_id:', userId)
      } else {
        console.warn(
          'Analytics: events saved but no user_id (user not authenticated)',
        )
      }
    })
    .catch((err) => {
      console.error('Analytics: flush failed', err)
      // Push events back into queue for next attempt (only for async failures)
      if (!forceSync) {
        eventsToSend.forEach((e) => eventQueue.push(e))
      }
    })
}

function isNavigationEvent(tag) {
  return navigationTags.some((prefix) => tag.startsWith(prefix))
}

export function initAnalytics() {
  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-track]')
    if (!el) return
    const tag = el.getAttribute('data-track')
    if (!tag) return

    const event = {
      event_type: 'click',
      tag,
      page: window.location.pathname,
      timestamp: new Date().toISOString(),
    }

    console.log('Analytics: data-track click captured:', tag)
    eventQueue.push(event)

    // Immediately flush navigation events that might cause page changes
    if (isNavigationEvent(tag)) {
      console.log('Analytics: navigation event detected, flushing immediately')
      setTimeout(() => flushEvents(), 50) // Small delay to allow event to be added to queue
    }
  })

  // Flush events before page unload
  window.addEventListener('beforeunload', () => {
    console.log(
      'Analytics: page unload detected, flushing events synchronously',
    )
    flushEvents(true) // Force synchronous flush
  })

  // Flush events on page visibility change (user switching tabs/apps)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      console.log('Analytics: page hidden, flushing events')
      flushEvents(true)
    }
  })

  // Flush events on page unload (critical for iOS - pagehide is more reliable than beforeunload)
  window.addEventListener('pagehide', () => {
    console.log('Analytics: page hiding, flushing events')
    flushEvents(true)
  })

  // Regular interval flush for non-navigation events
  setInterval(flushEvents, FLUSH_INTERVAL_MS)
}

/**
 * Track a custom analytics event
 * @param {string} tag - Event tag (e.g., 'button.click', 'page.view')
 * @param {Object} metadata - Event metadata. Special handling:
 *   - `page`: If provided, overrides the default (window.location.pathname)
 *             and is moved to the top-level event object instead of metadata
 */
export function track(tag, metadata = {}) {
  const { page: pageOverride, ...restMetadata } = metadata || {}
  const event = {
    event_type: 'custom',
    tag,
    page: pageOverride || window.location.pathname,
    metadata: restMetadata,
    timestamp: new Date().toISOString(),
  }

  console.log('Analytics: manual track event:', tag, metadata)
  eventQueue.push(event)

  // Immediately flush navigation events
  if (isNavigationEvent(tag)) {
    console.log(
      'Analytics: manual navigation event detected, flushing immediately',
    )
    setTimeout(() => flushEvents(), 50)
  }
}
